import React, { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { Send, MessageCircle, ShieldCheck } from "lucide-react";

function Message({ msg, isMe }) {
  return (
    <div className={`flex ${isMe ? "justify-end" : "justify-start"} mb-3`}>
      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
        isMe
          ? "bg-orange-500 text-white rounded-br-sm"
          : "bg-white border border-slate-200 text-slate-900 rounded-bl-sm"
      }`}>
        {!isMe && (
          <div className="flex items-center gap-1.5 mb-1">
            <ShieldCheck className="w-3 h-3 text-orange-500" />
            <span className="text-[10px] font-bold text-orange-500 uppercase tracking-wide">Support DepannRoute</span>
          </div>
        )}
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.body}</p>
        <div className={`text-[10px] mt-1 ${isMe ? "text-orange-200" : "text-slate-400"}`}>
          {new Date(msg.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
}

export default function Support() {
  const { user } = useAuth();
  const [messages, setMessages]   = useState([]);
  const [input,    setInput]      = useState("");
  const [sending,  setSending]    = useState(false);
  const bottomRef = useRef(null);
  const pollRef   = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/support/messages");
      setMessages(data);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 3000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setSending(true);
    try {
      await api.post("/support/messages", { body: input.trim() });
      setInput("");
      await load();
    } catch (err) {
      console.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="flex pt-16">
        <Sidebar />
        <main className="flex-1 flex flex-col max-w-2xl" style={{ height: "calc(100vh - 4rem)" }}>
          {/* Header */}
          <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-3 flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-slate-900">Support DepannRoute</div>
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                En ligne · Répond généralement en quelques minutes
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {messages.length === 0 ? (
              <div className="text-center py-16">
                <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <div className="font-display font-bold text-slate-900">Besoin d'aide ?</div>
                <p className="text-sm text-slate-500 mt-1 max-w-xs mx-auto">
                  Notre équipe support est disponible pour répondre à toutes vos questions.
                </p>
              </div>
            ) : (
              messages.map(msg => (
                <Message
                  key={msg.msg_id}
                  msg={msg}
                  isMe={msg.sender_id === user?.user_id}
                />
              ))
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <form onSubmit={send} className="bg-white border-t border-slate-200 px-4 py-3 flex gap-3 flex-shrink-0">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Écrivez votre message…"
              className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="w-10 h-10 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-xl flex items-center justify-center transition-colors flex-shrink-0"
            >
              {sending
                ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <Send className="w-4 h-4" />
              }
            </button>
          </form>
        </main>
      </div>
    </div>
  );
}
